import { cloneElement, type ReactElement } from 'react';
import { Tooltip } from '@mantine/core';

import { I, type IconProps } from '@/components/ri/icons';
import { ScalePadLogo } from '@/components/ri/ScalePadLogo';
import { ScalePadMark } from '@/components/ri/ScalePadMark';

export type DesktopNavKey = 'home' | 'accounts' | 'quotes' | 'approvals' | 'events' | 'admin' | 'how-to' | 'whats-new';

interface NavItem {
  k: DesktopNavKey;
  label: string;
  icon: ReactElement<IconProps>;
}

export interface DesktopSidebarProps {
  active?: DesktopNavKey;
  collapsed?: boolean;
  badges?: Partial<Record<DesktopNavKey, number>>;
  showAdmin?: boolean;
  showAccounts?: boolean;
  showApprovals?: boolean;
  showEvents?: boolean;
  onNavigate?: (key: DesktopNavKey) => void;
  onToggleCollapse?: () => void;
  showToggle?: boolean;
}

const PRIMARY: NavItem[] = [
  { k: 'home', label: 'Home', icon: I.home as ReactElement<IconProps> },
  { k: 'quotes', label: 'Quotes', icon: I.doc as ReactElement<IconProps> },
  { k: 'events', label: 'Events', icon: I.calendar as ReactElement<IconProps> },
  { k: 'approvals', label: 'Approvals', icon: I.shield as ReactElement<IconProps> },
  { k: 'accounts', label: 'Accounts', icon: I.bldg as ReactElement<IconProps> },
];

const RESOURCES: NavItem[] = [
  { k: 'how-to', label: 'How to', icon: I.doc as ReactElement<IconProps> },
  { k: 'whats-new', label: 'What’s new', icon: I.clock as ReactElement<IconProps> },
];

const ADMIN: NavItem[] = [{ k: 'admin', label: 'Admin', icon: I.cog as ReactElement<IconProps> }];

export function DesktopSidebar({
  active = 'home',
  collapsed = false,
  badges = {},
  showAdmin = false,
  showAccounts = false,
  showApprovals = false,
  showEvents = false,
  onNavigate,
  onToggleCollapse,
  showToggle = false,
}: DesktopSidebarProps) {
  const primaryItems = PRIMARY.filter((it) => {
    if (it.k === 'accounts') return showAccounts;
    if (it.k === 'approvals') return showApprovals;
    if (it.k === 'events') return showEvents;
    return true;
  });
  const renderItem = (it: NavItem, showBadge: boolean) => {
    const a = it.k === active;
    const badge = badges[it.k];
    const button = (
      <button
        key={it.k}
        onClick={() => onNavigate?.(it.k)}
        aria-label={it.label}
        aria-current={a ? 'page' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10,
          background: a ? 'var(--surface-raised)' : 'transparent',
          border: 0,
          padding: collapsed ? '8px 0' : '7px 9px',
          borderRadius: 7,
          cursor: 'pointer',
          color: a ? 'var(--text-primary)' : 'var(--text-secondary)',
          fontSize: 13,
          fontWeight: a ? 600 : 500,
          position: 'relative',
          boxShadow: a ? 'var(--shadow-xs)' : 'none',
        }}
      >
        {cloneElement(it.icon, { size: 17, stroke: 1.8 })}
        {!collapsed && <span style={{ flex: 1, textAlign: 'left' }}>{it.label}</span>}
        {!collapsed && showBadge && badge != null && (
          <span
            className="ri-mono"
            style={{
              fontSize: 10.5,
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--warning)',
              color: '#fff',
              fontWeight: 600,
            }}
          >
            {badge}
          </span>
        )}
        {collapsed && showBadge && badge != null && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 10,
              width: 7,
              height: 7,
              borderRadius: 999,
              background: 'var(--warning)',
            }}
          />
        )}
      </button>
    );
    if (collapsed) {
      return (
        <Tooltip key={it.k} label={it.label} position="right" withArrow openDelay={150}>
          {button}
        </Tooltip>
      );
    }
    return button;
  };

  return (
    <div
      style={{
        width: collapsed ? 64 : 220,
        flex: '0 0 auto',
        background: 'var(--surface-sunken)',
        borderRight: '1px solid var(--border)',
        padding: collapsed ? '14px 8px' : '14px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        height: '100%',
        transition: 'width 0.18s ease, padding 0.18s ease',
      }}
    >
      <button
        type="button"
        onClick={() => onNavigate?.('home')}
        aria-label="Go to home"
        title="Go to home"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 9,
          padding: collapsed ? '4px 0 14px' : '4px 6px 14px',
          width: '100%',
          background: 'transparent',
          border: 0,
          borderBottom: '1px solid var(--border-subtle)',
          marginBottom: 8,
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        {collapsed ? (
          <ScalePadMark size={26} />
        ) : (
          <>
            <ScalePadLogo size="xs" />
            <span
              aria-hidden
              style={{
                width: 1,
                height: 16,
                background: 'var(--border-strong)',
                flex: '0 0 auto',
              }}
            />
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 500,
                fontSize: 15,
                letterSpacing: 0,
                lineHeight: 1,
              }}
            >
              Meridian
            </span>
          </>
        )}
      </button>
      {primaryItems.map((it) => renderItem(it, true))}
      <div style={{ flex: 1 }} />
      {!collapsed && <div className="ri-eyebrow" style={{ padding: '8px 9px' }}>Resources</div>}
      {RESOURCES.map((it) => renderItem(it, false))}
      {showAdmin && ADMIN.map((it) => renderItem(it, false))}
      {showToggle && onToggleCollapse && (
        <Tooltip
          label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          position="right"
          withArrow
          openDelay={150}
          disabled={!collapsed}
        >
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            style={{
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 10,
              background: 'transparent',
              border: 0,
              padding: collapsed ? '8px 0' : '7px 9px',
              borderRadius: 7,
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {cloneElement(
              (collapsed ? I.chevR : I.chevL) as ReactElement<IconProps>,
              { size: 17, stroke: 1.8 },
            )}
            {!collapsed && <span style={{ flex: 1, textAlign: 'left' }}>Collapse</span>}
          </button>
        </Tooltip>
      )}
    </div>
  );
}
